import React, { useRef } from "react";
import { useNavigate } from "react-router";

import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { translation } from "@/i18n";
import { nsView } from "@/i18n/locales/view";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { useUpdateVersion } from "../../../../../domain/Version";
import {
  View,
  useRecursiveDeleteView,
  useUpdateView,
} from "../../../../../domain/View";
import { ManagedTransaction } from "../../../../../firebase/firestore";
import { DialogPhaseStatesType, useDialog } from "../../../helpers/hooks";

const useTranslation = translation.use(nsView);

export const DialogViewDelete: React.FC<
  {
    views: View[];
    selectedViewIds: string[];
    setSelectedViewIds: React.Dispatch<React.SetStateAction<string[]>>;
  } & DialogPhaseStatesType
> = ({
  views,
  selectedViewIds,
  setSelectedViewIds,
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId, versionId, viewId: nowViewId } = useAppRouteParams();
  const recursiveDeleteView = useRecursiveDeleteView();
  const updateView = useUpdateView();
  const updateVersion = useUpdateVersion();
  const { versions } = useVersions();
  const version = versions.find((v) => v.id === versionId);

  const { renderDialogs } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const isInitialView =
    !!version?.initialViewId && selectedViewIds.includes(version.initialViewId);

  const runDelete = async () => {
    if (nowViewId && selectedViewIds.includes(nowViewId)) {
      navigate(`/organizations/${organizationId}/versions/${versionId}`);
    }
    setDialogPhase("in_progress");
    const otherViews = views.filter(({ id }) => !selectedViewIds.includes(id));
    try {
      const deleteQueueList = await Promise.all(
        selectedViewIds.map((viewId) => recursiveDeleteView(viewId, {}))
      );
      await ManagedTransaction.runTransaction(async (transaction) => {
        // view削除
        for (const deleteQueue of deleteQueueList) {
          await deleteQueue.exec(transaction);
        }
        // ソート番号振り直し
        for (const [index, view] of otherViews.entries()) {
          await updateView(
            { ...view, sort: index },
            { viewId: view.id },
            { transaction }
          );
        }
      });
      // 削除したビューが初期ビューに設定されていた場合、初期ビュー設定を解除する
      if (isInitialView) {
        await updateVersion({ ...version, initialViewId: "" }, {}, {});
      }
      showSuccessNotification(t.view.deleteDialog.success());
      setSelectedViewIds([]);
    } catch (error) {
      showErrorNotification(t.view.deleteDialog.error(), error);
    }
    setDialogPhase(null);
  };

  // 削除を実行すると Firestore の更新が先に反映されて views / version から対象が消えるため、
  // 確認時点の対象を保持し、ダイアログを閉じ切るまで同じ内容を表示し続ける
  const confirmedTargetRef = useRef({
    title: t.view.fallbackName(),
    isInitialView: false,
  });
  if (dialogPhase === "confirm") {
    confirmedTargetRef.current = {
      title:
        views.find(({ id }) => id === selectedViewIds[0])?.title ??
        t.view.fallbackName(),
      isInitialView,
    };
  }
  const confirmedTarget = confirmedTargetRef.current;

  const displayMessage = t.view.deleteDialog.message({
    viewName: confirmedTarget.title,
  });
  const additionalElement = confirmedTarget.isInitialView ? (
    <div style={{ color: "var(--figma-color-status-error)", marginTop: 8 }}>
      {t.view.deleteDialog.initialViewWarning()}
    </div>
  ) : undefined;

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.view.deleteDialog.title(),
          displayMessage,
          buttonText: t.view.deleteDialog.button(),
          inProgressButtonText: t.view.deleteDialog.inProgressButton(),
          onClick: runDelete,
          onClickCancel: () => {
            setSelectedViewIds([]);
          },
          isThemeRed: true,
          element: additionalElement,
        },
      })}
    </>
  );
};
